/** The card a right press puts on the screen.
 *
 * A leaf: it is handed an `InfoCard` and a point, and it draws. Deciding what
 * the card says is `info.ts`; deciding when it is up is the shell's.
 *
 * The only thing this component reasons about is where to put itself. A popup
 * anchored naively at the pointer is half off the screen the moment the player
 * right-clicks near an edge - which is exactly where the interesting things on
 * a map are, because the camera is centred on the hero and the enemy is at the
 * margin. So it measures itself and flips.
 */

import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import { Icon } from "../theme/components.js";
import { METRICS } from "../theme/tokens.js";
import type { InfoCard } from "./info.js";

export interface InfoPopupProps {
  readonly card: InfoCard;
  /** Viewport coordinates of the press. */
  readonly x: number;
  readonly y: number;
  /** Dismissed by the shell; the card itself never closes. */
  readonly onDismiss?: () => void;
}

/** Kept clear of the pointer so the card never sits under the cursor. */
const OFFSET = 14;
const MARGIN = 8;
const MAX_WIDTH = 300;

const STYLE: Readonly<Record<string, CSSProperties>> = {
  host: {
    position: "fixed",
    zIndex: 40,
    maxWidth: `${String(MAX_WIDTH)}px`,
    // A card is information, not a control: it must never eat the pointer
    // events the shell is using to decide when to take it down again.
    pointerEvents: "none",
    padding: "var(--hm-pad)",
  },
  head: { display: "flex", gap: "8px", alignItems: "flex-start", marginBottom: "4px" },
  title: { font: "600 13px/1.25 ui-sans-serif, system-ui, sans-serif", color: "#f3ddab" },
  subtitle: {
    font: "500 11px/1.3 ui-sans-serif, system-ui, sans-serif",
    color: "#c3ae86", marginTop: "1px",
  },
  line: {
    font: "400 11px/1.4 ui-sans-serif, system-ui, sans-serif",
    color: "#e2d4b4", marginTop: "4px",
  },
  table: { marginTop: "6px", display: "grid", gridTemplateColumns: "auto 1fr", gap: "1px 10px" },
  label: { font: "400 11px/1.5 ui-sans-serif, system-ui, sans-serif", color: "#b8a47e" },
  value: {
    font: "600 11px/1.5 ui-sans-serif, system-ui, sans-serif",
    color: "#f0e2c0", textAlign: "right",
  },
  footer: {
    font: "400 10px/1.4 ui-sans-serif, system-ui, sans-serif",
    color: "#9c8a68", marginTop: "6px", fontStyle: "italic",
  },
};

export function InfoPopup(props: InfoPopupProps): JSX.Element {
  const { card } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);

  // Laid out before paint rather than in an effect: a card that is drawn at the
  // pointer and then jumps to its flipped position is a card that flickers, and
  // this one is up for a fraction of a second at a time.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const box = el.getBoundingClientRect();
    // The pointer and the viewport are in viewport CSS pixels; this card is
    // positioned in the app root's own pixels. Nothing scales the root today
    // (the zoom-compensating transform is gone), so `k` is 1; it is measured
    // rather than assumed so a scaled ancestor cannot put the card off-glass.
    const k = box.width > 0 ? el.offsetWidth / box.width : 1;
    const vw = window.innerWidth * k, vh = window.innerHeight * k;
    const w = box.width * k, h = box.height * k;
    const px = props.x * k, py = props.y * k;
    let left = px + OFFSET;
    let top = py + OFFSET;
    if (left + w > vw - MARGIN) left = px - OFFSET - w;
    if (top + h > vh - MARGIN) top = py - OFFSET - h;
    setPlace({
      left: Math.max(MARGIN, Math.min(left, vw - MARGIN - w)),
      top: Math.max(MARGIN, Math.min(top, vh - MARGIN - h)),
    });
  }, [props.x, props.y, card]);

  return (
    /* A bare div with the kit's panel class rather than <Panel>: this one needs
       a ref to measure itself and a `role` to announce itself, and neither is
       worth widening the shared component's contract for. */
    <div
      ref={ref}
      className="hm-panel"
      role="tooltip"
      aria-live="polite"
      style={{
        ...STYLE.host,
        // Off-screen until measured, rather than at (0,0): the first frame of a
        // card in the corner is more noticeable than no card at all.
        left: place?.left ?? -9999,
        top: place?.top ?? -9999,
        visibility: place === null ? "hidden" : "visible",
      }}
    >
      <div style={STYLE.head}>
        {card.icon !== null ? (
          <Icon atlasKey={card.icon} size={METRICS.iconLarge} title="" />
        ) : null}
        <div style={{ minWidth: 0 }}>
          <div style={STYLE.title}>{card.title}</div>
          {card.subtitle !== null ? <div style={STYLE.subtitle}>{card.subtitle}</div> : null}
        </div>
      </div>

      {card.lines.map((l, i) => (
        <div key={`${String(i)}:${l}`} style={STYLE.line}>{l}</div>
      ))}

      {card.rows.length > 0 ? (
        <div style={STYLE.table}>
          {card.rows.map((r, i) => (
            <div key={`${String(i)}:${r.label}`} style={{ display: "contents" }}>
              <span style={STYLE.label}>{r.label}</span>
              <span style={STYLE.value}>{r.value}</span>
            </div>
          ))}
        </div>
      ) : null}

      {card.footer !== null ? <div style={STYLE.footer}>{card.footer}</div> : null}
    </div>
  );
}
